// NewsTalk (c) Copyright 2023 by Qwixotic
// 
// File:   index.js
// Author: Percipient Reverence
// Date:   30-Apr-2023
//
// Description:
//
//   This is where everything starts.  Routing to the webpages is defined here.
//
//   About how function is divided into modules, this outline of some of the routes should make
//   clear how it is all organized:
//
//     Route               Component           Module              Description
//     ----------------------------------------------------------------------------------
//     /                   NewsTalkClientEP    NewsTalkClient.js   All media outlets
//     /:MOID              MediaOutletEP       MediaOutlet.js      A specified media outlet's sections
//     /MediaOutlet        MediaOutletEP       MediaOutlet.js      The default media outlet's sections
//     /MediaOutlet/:MOID  MediaOutletEP       MediaOutlet.js      A specified media outlet's sections
//     /Section            SectionEP           Section.js          The default section's articles
//     /Section/:SID       SectionEP           Section.js          A specified section's articles
//     /Article            ArticleEP           Article.js          The default article's discussion thread
//     /Article/:AID       ArticleEP           Article.js          A specified article's discussion thread
//
//   A default means it determines which one from a cookie.  If no cookie then as far as the user is
//   concerned a random one is chosen, or maybe we'll serve an error page.

import React, {useEffect, useState} from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet
} from 'react-router-dom'
import ReactDOM from 'react-dom/client'
import reportWebVitals from './reportWebVitals'

// Imports local to NewsTalk
import { ConfigVars,
         myFetch}                     from './lib/Util'
import { NewsTalkHomeEP }             from './NewsTalkHome'
import { MediaOutletEP }              from './MediaOutlet'
import { SectionEP }                  from './Section'
import { ArticleMIDEP,
         ArticleAIDEP }               from './Article'
import { CommentReplyEP }             from './Comment'
import { WebsiteHeader, Copyright }   from './lib/Components'
import { AccountSettingsEP,
         EmailValidationEP,
         PasswordEnterEP,
         RegValidationEP}             from './AccountSettings'

const NotFoundEP = () => (
  <b>
    Page Not Found
  </b>
)

// This component is the outer layer, so we have to declare the signin credentials here so they can
// be passed to inner components
const Layout = (props) => {
  const [signinCredentials, setSigninCredentials] = useState({idnum: 0})
  const [siteData, setSiteData] = useState(null)
  const wrappedSetSigninCredentials = (arg) => {
    console.log('Calling setSigninCredentials...')
    setSigninCredentials(arg)
  }

  // Fetch site data
  useEffect(() => {
    myFetch('/nt/fetchSiteData')
      .then(res => {
        if (!res.ok) {
          throw Error(res.statusText)
        }
        return res.json()
      })
      .then(siteDataResult => {
        if (siteDataResult.status === 'Success') {
          setSiteData(siteDataResult.data)
        } else {
          throw Error(siteDataResult.message)
        }
      })
      .catch((err) => {
        console.log(`*** ERROR *** Failure while fetching site data information: ${err}`)
      })
  }, [])

  return (
    !siteData
      ? null
      : <>
          <WebsiteHeader key='outer_1' signinCredentials={signinCredentials} credentialSetter={wrappedSetSigninCredentials}
                         siteData={siteData} nosignin={props.nosignin}/>
          <Outlet key='outer_2' context={{siteData: siteData, signinCredentials: signinCredentials,
                                          credentialSetter: wrappedSetSigninCredentials}} />
          <Copyright key='outer_3' siteData={siteData} />
        </>
  )
}

// ASE: Account Settings Edit
const Endpoints = () => (
  <Routes>
    <Route path="/ASE" element={<Layout nosignin={true}/>} >
      <Route path="EmailValidation/:MBRID/:Code"   element={<EmailValidationEP   />} />
      <Route path="PasswordEnter/:MBRID/:Code"     element={<PasswordEnterEP     />} />
      <Route path="RegValidation/:MBRID/:Code"     element={<RegValidationEP   />} />
    </Route>
    <Route path="/" element={<Layout />} >
      <Route path=""                               element={<NewsTalkHomeEP      />} />
      <Route path="MediaOutlet"                    element={<MediaOutletEP       />} />
      <Route path="MediaOutlet/:MOID"              element={<MediaOutletEP       />} />
      <Route path="Section"                        element={<SectionEP           />} />
      <Route path="Section/:SID"                   element={<SectionEP           />} />
      <Route path="Article/MID/:SID/:MID"          element={<ArticleMIDEP        />} />
      <Route path="Article/AID/:SID/:AID/:pageNum" element={<ArticleAIDEP        />} />
      <Route path="CommentReply/:SID/:AID"         element={<CommentReplyEP      />} />
      <Route path="CommentReply/:SID/:AID/:MID"    element={<CommentReplyEP      />} />
      <Route path="AccountSettings"                element={<AccountSettingsEP   />} />
      <Route path="*"                              element={<NotFoundEP          />} />
    </Route>
  </Routes>
)

const NewsTalk = () => {
  return (
    <Router key='router'>
      <Endpoints/>
    </Router>
  )
}    

// React.StrictMode is very useful for detecting issues and errors, but when using console.log to
// track what's happening it can be very misleading because things that actually only happen once or
// twice appear to be happening multiple times.  When working on things like minimizing rerendering
// it is a good idea to remove the React.Strictmode component.
ReactDOM.createRoot(document.getElementById('root')).render(
  ConfigVars.StrictMode
    ? (
      <React.StrictMode>
        <NewsTalk/>
      </React.StrictMode>
    )
    : (
      <NewsTalk/>
    )
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
